// node_modules
import {
    FC, useCallback, useContext, useEffect
} from "react";
import { Redirect, Route, Switch, useRouteMatch } from "react-router-dom";
// Components
import { CasePhaseWrapper } from "Components/Case/Results/CasePhaseWrapper/CasePhaseWrapper";
import { ResultsMainContent } from "./ResultsMainContent";
// Constants
import { CasePhaseConstants, HubConstants } from "Constants";
// Contexts
import { CaseContext, AuthContext } from "Stores";
import { WebsocketContext } from "Providers";
// Helpers
import { ToastHelperSingleton } from "Helpers";
// Types
import { TNotificationMessageDTO, TNewTechnologyVoteDTO, TNewTechnologyRateDTO } from "Types";
// Enums
import { CaseActionTypeEnum } from "Enums";
// Interfaces
import { IUpdateTechnologyRateAction, IUpdateTechnologyVoteAction } from "Interfaces";

export const Results: FC = () => {
    // Contexts
    const { caseState, caseDispatch } = useContext(CaseContext);
    const { authState } = useContext(AuthContext);
    const { webSocketController } = useContext(WebsocketContext);

    // Const
    const technologyVotesResourceName = caseState.selectedCaseId ? `${HubConstants.TECHNOLOGY_VOTES_RESOURCE_NAME_PREFIX}${caseState.selectedCaseId}` : "";
    const technologyRatesResourceName = caseState.selectedCaseId ? `${HubConstants.TECHNOLOGY_RATES_RESOURCE_NAME_PREFIX}${caseState.selectedCaseId}` : "";
    
    // Hooks
    const { url } = useRouteMatch();

    // Logic
    const handleNotificationMessages = useCallback((notificationMessage: TNotificationMessageDTO): void => {
        ToastHelperSingleton.handleNotificationMessage(notificationMessage, authState.userEmail);
    }, [authState]);

    const handleTechnologyVoteUpdated = useCallback((newTechnologyVote: TNewTechnologyVoteDTO) => {
        const isCurrentUserVote = newTechnologyVote.userEmailWhoVoted.trim().toLowerCase() === authState.userEmail.trim().toLowerCase();
        // update technology vote in CaseContext (in order to re-render components which use this vote)
        caseDispatch({ 
            type: CaseActionTypeEnum.UpdateTechnologyVoteAction, 
            vote: newTechnologyVote.technologyVote, 
            technologyId: newTechnologyVote.technologyId,
            isCurrentUserVote,
            userEmailWhoVoted: newTechnologyVote.userEmailWhoVoted
        } as IUpdateTechnologyVoteAction);
    }, [caseDispatch, authState.userEmail]);

    const handleTechnologyRateUpdated = useCallback((newTechnologyRate: TNewTechnologyRateDTO) => {
        const isCurrentUserRate = newTechnologyRate.userEmailWhoVoted.trim().toLowerCase() === authState.userEmail.trim().toLowerCase();
        // update technology rate in CaseContext (in order to re-render components which use this rate)
        caseDispatch({ 
            type: CaseActionTypeEnum.UpdateTechnologyRateAction, 
            rate: newTechnologyRate.technologyRate, 
            technologyId: newTechnologyRate.technologyId,
            isCurrentUserRate,
            userEmailWhoVoted: newTechnologyRate.userEmailWhoVoted
        } as IUpdateTechnologyRateAction);
    }, [caseDispatch, authState.userEmail]);

    // manage web sockets related events
    useEffect(() => {
        // safety-check
        if (!technologyVotesResourceName || !technologyRatesResourceName) return;
        
        // we add two handlers to listen on update and notification messages related to technology votes and rates resource names
        webSocketController.addHandler(`${HubConstants.UPDATE_PREFIX}${technologyVotesResourceName}`, 
            handleTechnologyVoteUpdated);
        webSocketController.addHandler(`${HubConstants.UPDATE_PREFIX}${technologyRatesResourceName}`, 
            handleTechnologyRateUpdated);
        webSocketController.addHandler(`${HubConstants.NOTIFICATION_MESSAGE_PREFIX}${technologyVotesResourceName}`, 
            handleNotificationMessages);
        webSocketController.addHandler(`${HubConstants.NOTIFICATION_MESSAGE_PREFIX}${technologyRatesResourceName}`, 
            handleNotificationMessages);
        
        // remove handlers on clean up time
        return () => {
            webSocketController.removeHandler(`${HubConstants.UPDATE_PREFIX}${technologyVotesResourceName}`, 
                handleTechnologyVoteUpdated);
            webSocketController.removeHandler(`${HubConstants.UPDATE_PREFIX}${technologyRatesResourceName}`, 
                handleTechnologyRateUpdated);
            webSocketController.removeHandler(`${HubConstants.NOTIFICATION_MESSAGE_PREFIX}${technologyVotesResourceName}`, 
                handleNotificationMessages);
            webSocketController.removeHandler(`${HubConstants.NOTIFICATION_MESSAGE_PREFIX}${technologyRatesResourceName}`, 
                handleNotificationMessages);
        };
    }, [handleNotificationMessages, handleTechnologyRateUpdated, handleTechnologyVoteUpdated, technologyRatesResourceName, technologyVotesResourceName, webSocketController]);




    return (
        <Switch>
            {caseState.selectedCase?.isPhaseTwoPublished ?
                <Route path={`${url}/phase2`}>
                    <CasePhaseWrapper phaseName={CasePhaseConstants.PHASE_2.toLowerCase()}>
                        <ResultsMainContent />
                    </CasePhaseWrapper>
                </Route>
                : null}
            {caseState.selectedCase?.isPhaseOnePublished ?
                <Route path={`${url}/phase1`}>
                    <CasePhaseWrapper phaseName={CasePhaseConstants.PHASE_1.toLowerCase()}>
                        <ResultsMainContent />
                    </CasePhaseWrapper>
                </Route>
                : null}
            {caseState.selectedCase?.isPhaseTwoPublished ?
                <Route>
                    <Redirect to={`${url}/phase2`} />
                </Route>
                :
                caseState.selectedCase?.isPhaseOnePublished ?
                    <Route>
                        <Redirect to={`${url}/phase1`} />
                    </Route>
                    :
                    <Route>
                        <div style={{width: "100%", height: "100%", textAlign: "center"}}>
                            <p>No results yet.</p>
                        </div> 
                    </Route>
            }
        </Switch>
    );
};
